/**
 * Hair Assessment Widget
 * Floating button → modal dialog → camera/upload → API → results + add to cart
 */

const MAX_PHOTOS = 3;
// Backend requires exactly 3 photos — anything less is rejected server-side,
// so gate the frontend on the same minimum.
const MIN_PHOTOS = 3;
const IS_MOBILE = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 640;

class HairAssessmentWidget {
  constructor(root) {
    this.root = root;
    this.apiEndpoint = root.dataset.apiEndpoint || 'https://hairtwin.beardsnshears.in/v1/hair-assessment';
    this.photos = [];
    this.stream = null;
    this.facingMode = 'environment'; // rear camera on mobile by default
    this.currentStep = 1;

    this.dialog = root.querySelector('#ha-dialog');
    this.trigger = root.querySelector('#ha-trigger');

    this.els = {
      stepIndicators: root.querySelectorAll('.ha-step-indicator'),
      step1: root.querySelector('#ha-step-1'),
      step2: root.querySelector('#ha-step-2'),
      step3: root.querySelector('#ha-step-3'),

      formSex: root.querySelector('#ha-sex'),
      formAge: root.querySelector('#ha-age-range'),
      formNotes: root.querySelector('#ha-notes'),
      toStep2Btn: root.querySelector('#ha-to-step2'),

      video: root.querySelector('#ha-video'),
      videoPlaceholder: root.querySelector('#ha-video-placeholder'),
      cameraCount: root.querySelector('#ha-camera-count'),
      startCameraBtn: root.querySelector('#ha-start-camera'),
      switchCameraBtn: root.querySelector('#ha-switch-camera'),
      shutterBtn: root.querySelector('#ha-shutter'),
      thumbnailsRow: root.querySelector('#ha-thumbnails'),
      uploadInput: root.querySelector('#ha-upload-input'),
      backStep1Btn: root.querySelector('#ha-back-step1'),
      submitBtn: root.querySelector('#ha-submit'),
      photoHint: root.querySelector('#ha-photo-hint'),

      loadingEl: root.querySelector('#ha-loading'),
      errorEl: root.querySelector('#ha-error'),
      errorMsg: root.querySelector('#ha-error-msg'),
      resultsEl: root.querySelector('#ha-results'),
      startOverBtns: root.querySelectorAll('.ha-start-over'),

      canvas: root.querySelector('#ha-capture-canvas'),
      dialogTitle: root.querySelector('#ha-dialog-title'),
    };

    this._bind();
  }

  _bind() {
    this.trigger?.addEventListener('click', () => this._open());

    // Close on backdrop click
    this.dialog?.addEventListener('click', (e) => {
      if (e.target === this.dialog) this._close();
    });

    // Close button
    this.root.querySelector('#ha-close-btn')?.addEventListener('click', () => this._close());

    this.els.toStep2Btn?.addEventListener('click', () => this._goToStep(2));
    this.els.backStep1Btn?.addEventListener('click', () => { this._stopCamera(); this._goToStep(1); });
    this.els.startCameraBtn?.addEventListener('click', () => this._startCamera());
    this.els.switchCameraBtn?.addEventListener('click', () => this._switchCamera());
    this.els.shutterBtn?.addEventListener('click', () => this._capturePhoto());
    this.els.uploadInput?.addEventListener('change', (e) => this._handleUpload(e));
    this.els.submitBtn?.addEventListener('click', () => this._submit());
    this.els.startOverBtns?.forEach((b) => b.addEventListener('click', () => this._reset()));

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.dialog?.open) this._close();
    });
  }

  _open() {
    // Freeze body scroll — same pattern as theme's dialog.js
    this._scrollY = window.scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this._scrollY}px`;

    this.dialog.showModal ? this.dialog.showModal() : (this.dialog.open = true);

    // Check for a previous assessment saved in localStorage
    const savedSession = localStorage.getItem('ha_session_id');
    const savedAnon    = localStorage.getItem('ha_anon_id');

    if (savedSession && savedAnon) {
      this._goToStep(3);
      this._fetchPreviousResults(savedSession, savedAnon);
    } else {
      this._goToStep(1);
      if (IS_MOBILE) {
        setTimeout(() => this._goToStep(2), 80);
        setTimeout(() => this._startCamera(), 160);
      }
    }
  }

  async _fetchPreviousResults(sessionId, anonId) {
    this.els.loadingEl?.classList.remove('ha-hidden');
    this.els.errorEl?.classList.add('ha-hidden');
    this.els.resultsEl?.classList.add('ha-hidden');

    try {
      const url = new URL(this.apiEndpoint);
      url.searchParams.set('session_id', sessionId);
      url.searchParams.set('anon_id', anonId);

      const res = await fetch(url.toString(), { method: 'GET' });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      this.els.loadingEl?.classList.add('ha-hidden');
      this._renderResults(data, true);
    } catch {
      // If fetch fails (expired session etc.), clear storage and start fresh
      this.els.loadingEl?.classList.add('ha-hidden');
      localStorage.removeItem('ha_session_id');
      localStorage.removeItem('ha_anon_id');
      this._goToStep(1);
      if (IS_MOBILE) {
        setTimeout(() => this._goToStep(2), 80);
        setTimeout(() => this._startCamera(), 160);
      }
    }
  }

  _close() {
    this._stopCamera();
    this.dialog.close ? this.dialog.close() : (this.dialog.open = false);
    // Restore scroll — same pattern as theme's dialog.js
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, this._scrollY || 0);
  }

  _goToStep(step) {
    this.currentStep = step;

    const titles = { 1: 'Your Info', 2: 'Take Photos', 3: 'Your Results' };
    if (this.els.dialogTitle) this.els.dialogTitle.textContent = titles[step] || 'Hair Assessment';

    this.els.step1?.classList.toggle('ha-hidden', step !== 1);
    this.els.step2?.classList.toggle('ha-hidden', step !== 2);
    this.els.step3?.classList.toggle('ha-hidden', step !== 3);

    this.els.stepIndicators.forEach((el, i) => {
      el.classList.remove('is-active', 'is-done');
      const n = i + 1;
      if (n < step) el.classList.add('is-done');
      if (n === step) el.classList.add('is-active');
    });

    if (step === 2) {
      this._renderThumbnails();
      this._updateSubmitState();
    }
  }

  async _startCamera() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: this.facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
        audio: false,
      });
      this.els.video.srcObject = this.stream;
      this.els.video.classList.remove('ha-hidden');
      this.els.videoPlaceholder?.classList.add('ha-hidden');
      this.els.startCameraBtn?.classList.add('ha-hidden');
      this.els.shutterBtn && (this.els.shutterBtn.disabled = false);
      this.els.switchCameraBtn?.classList.remove('ha-hidden');
    } catch {
      this.els.startCameraBtn && (this.els.startCameraBtn.textContent = 'Retry Camera');
    }
  }

  _stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.els.video) {
      this.els.video.srcObject = null;
      this.els.video.classList.add('ha-hidden');
    }
    this.els.videoPlaceholder?.classList.remove('ha-hidden');
    this.els.startCameraBtn?.classList.remove('ha-hidden');
    this.els.shutterBtn && (this.els.shutterBtn.disabled = true);
    this.els.switchCameraBtn?.classList.add('ha-hidden');
  }

  async _switchCamera() {
    this._stopCamera();
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    await this._startCamera();
  }

  _capturePhoto() {
    if (this.photos.length >= MAX_PHOTOS) return;
    const video = this.els.video;
    const canvas = this.els.canvas;
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      this.photos.push({ blob, dataUrl: canvas.toDataURL('image/jpeg', 0.9) });
      this._renderThumbnails();
      this._updateSubmitState();
      // Flash ring
      const s = this.els.shutterBtn;
      s.style.borderColor = '#4ade80';
      setTimeout(() => { s.style.borderColor = ''; }, 280);
      if (this.photos.length >= MAX_PHOTOS) s.disabled = true;
    }, 'image/jpeg', 0.9);
  }

  _handleUpload(e) {
    const files = Array.from(e.target.files || []).slice(0, MAX_PHOTOS - this.photos.length);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        this.photos.push({ blob: file, dataUrl: ev.target.result });
        this._renderThumbnails();
        this._updateSubmitState();
        if (this.photos.length >= MAX_PHOTOS && this.els.shutterBtn) {
          this.els.shutterBtn.disabled = true;
        }
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  }

  _renderThumbnails() {
    const row = this.els.thumbnailsRow;
    row.innerHTML = '';

    this.photos.forEach((photo, i) => {
      const wrap = document.createElement('div');
      wrap.className = 'ha-thumb';
      const img = document.createElement('img');
      img.src = photo.dataUrl;
      img.alt = `Photo ${i + 1}`;
      const btn = document.createElement('button');
      btn.className = 'ha-thumb-remove';
      btn.innerHTML = '&times;';
      btn.setAttribute('aria-label', `Remove photo ${i + 1}`);
      btn.addEventListener('click', () => {
        this.photos.splice(i, 1);
        if (this.stream && this.photos.length < MAX_PHOTOS && this.els.shutterBtn) {
          this.els.shutterBtn.disabled = false;
        }
        this._renderThumbnails();
        this._updateSubmitState();
      });
      wrap.appendChild(img);
      wrap.appendChild(btn);
      row.appendChild(wrap);
    });

    for (let i = this.photos.length; i < MAX_PHOTOS; i++) {
      const empty = document.createElement('div');
      empty.className = 'ha-thumb-empty';
      empty.textContent = '+';
      row.appendChild(empty);
    }

    if (this.els.cameraCount) {
      this.els.cameraCount.textContent = `${this.photos.length}/${MAX_PHOTOS}`;
    }

    if (this.els.photoHint) {
      const need = MIN_PHOTOS - this.photos.length;
      if (need > 0) {
        this.els.photoHint.textContent = `${need} more photo${need > 1 ? 's' : ''} needed`;
      } else if (this.photos.length < MAX_PHOTOS) {
        this.els.photoHint.textContent = `Good! Add 1 more for better accuracy`;
      } else {
        this.els.photoHint.textContent = `${MAX_PHOTOS} photos ready`;
      }
    }
  }

  _updateSubmitState() {
    if (this.els.submitBtn) this.els.submitBtn.disabled = this.photos.length < MIN_PHOTOS;
  }

  async _submit() {
    const sex = this.els.formSex?.value || 'male';
    const ageRange = this.els.formAge?.value || '';
    const notes = this.els.formNotes?.value || '';

    this._goToStep(3);
    this._stopCamera();
    this.els.loadingEl?.classList.remove('ha-hidden');
    this.els.errorEl?.classList.add('ha-hidden');
    this.els.resultsEl?.classList.add('ha-hidden');

    this._startLoadingCycler();

    const fd = new FormData();
    fd.append('sex', sex);
    if (ageRange) fd.append('age_range', ageRange);
    if (notes) fd.append('notes', notes);

    // Pass saved session/anon IDs so the backend can return cached results instantly
    const savedSession = localStorage.getItem('ha_session_id');
    const savedAnon    = localStorage.getItem('ha_anon_id');
    if (savedSession) fd.append('session_id', savedSession);
    if (savedAnon)    fd.append('anon_id',    savedAnon);

    // Attach live Shopify products so the API can score and rank them
    const products = window.__haProducts;
    if (Array.isArray(products) && products.length > 0) {
      const apiProducts = products.map(({ id, product_name, product_description, original_price, discounted_price, key_benefits, how_to_use }) => ({
        id, product_name, product_description, original_price, discounted_price, key_benefits, how_to_use,
      }));
      fd.append('products', JSON.stringify(apiProducts));
    }

    this.photos.forEach((p) => fd.append('images', p.blob, 'photo.jpg'));

    try {
      const res = await fetch(this.apiEndpoint, { method: 'POST', body: fd });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const data = await res.json();
      this._stopLoadingCycler();
      this.els.loadingEl?.classList.add('ha-hidden');
      this._renderResults(data);
    } catch (err) {
      this._stopLoadingCycler();
      this.els.loadingEl?.classList.add('ha-hidden');
      this.els.errorEl?.classList.remove('ha-hidden');
      if (this.els.errorMsg) this.els.errorMsg.textContent = ' ' + (err.message || 'Something went wrong.');
    }
  }

  // Cycles through status messages + animates progress bar over ~30 s
  _startLoadingCycler() {
    const steps = [
      { text: 'Analysing your photos…',                    sub: 'Reading hair patterns and scalp condition',  pct: 8  },
      { text: 'Detecting hair loss stage…',                sub: 'Mapping against Norwood-Hamilton scale',      pct: 25 },
      { text: 'Identifying affected regions…',             sub: 'Frontal, crown, vertex and temples',          pct: 42 },
      { text: 'Fetching your product catalogue…',          sub: 'Loading personalised recommendations',        pct: 58 },
      { text: 'Filtering products for your condition…',    sub: 'Matching ingredients to your hair profile',   pct: 72 },
      { text: 'Ranking recommendations…',                  sub: 'Prioritising by effectiveness for your stage',pct: 86 },
      { text: 'Almost there…',                             sub: 'Putting your results together',               pct: 95 },
    ];

    const textEl = this.root.querySelector('#ha-loading-text');
    const subEl  = this.root.querySelector('#ha-loading-sub');
    const barEl  = this.root.querySelector('#ha-loading-bar');

    let idx = 0;
    const apply = () => {
      if (!textEl) return;
      const s = steps[idx];
      textEl.textContent = s.text;
      if (subEl)  subEl.textContent  = s.sub;
      if (barEl)  barEl.style.width  = s.pct + '%';
      idx = Math.min(idx + 1, steps.length - 1);
    };

    apply(); // show first step immediately
    this._loadingInterval = setInterval(apply, 4500);
  }

  _stopLoadingCycler() {
    clearInterval(this._loadingInterval);
    this._loadingInterval = null;
    const barEl = this.root.querySelector('#ha-loading-bar');
    if (barEl) barEl.style.width = '100%';
  }

  _renderResults(data, isPrevious = false) {
    const el = this.els.resultsEl;
    el.innerHTML = '';
    el.classList.remove('ha-hidden');

    // Persist session so returning users get their results automatically
    if (data.session_id) localStorage.setItem('ha_session_id', data.session_id);
    if (data.anon_id)    localStorage.setItem('ha_anon_id',    data.anon_id);

    // Store full data for use when user unlocks via OTP
    this._pendingData = data;

    // Banner shown when loading a previously saved assessment
    if (isPrevious) {
      const banner = document.createElement('div');
      banner.className = 'ha-returning-banner';
      banner.innerHTML = `<span>Showing your previous assessment</span>`;
      el.appendChild(banner);
    }

    // Trust the API's otp_verified flag; keep localStorage in sync
    const verified = data.otp_verified === true || localStorage.getItem('ha_verified') === '1';
    if (data.otp_verified === true) localStorage.setItem('ha_verified', '1');

    if (verified) {
      this._renderFull(data, el);
    } else {
      this._renderPreview(data, el);
    }
  }

  // ── Preview: stage badge + blurred description + locked products + unlock CTA ──
  _renderPreview(data, el) {
    const s = data.staging || {};
    const products = data.recommended_products || [];
    const confidence = Math.round((s.confidence || 0) * 100);

    const card = document.createElement('div');
    card.className = 'ha-assessment-card';
    card.innerHTML = `
      <p class="ha-section-label">Assessment Result</p>
      <div class="ha-stage-header">
        <div class="ha-stage-badge">
          <div class="ha-stage-badge-label">Stage</div>
          <div class="ha-stage-badge-value">${s.stage_numeric ?? '—'}</div>
          <div class="ha-stage-badge-scale">${this._fmt(s.scale)}</div>
        </div>
        <div class="ha-stage-info">
          <h3 class="ha-stage-name">${s.stage || ''}</h3>
          <p class="ha-confidence-row">Confidence: ${confidence}%</p>
          <div class="ha-confidence-bar">
            <div class="ha-confidence-fill" style="width:${confidence}%"></div>
          </div>
        </div>
      </div>
      ${s.pattern_description ? `
        <div class="ha-preview-fade">
          <p class="ha-description">${s.pattern_description}</p>
        </div>` : ''}
    `;
    el.appendChild(card);

    // Locked product previews
    if (products.length) {
      const sec = document.createElement('div');
      sec.innerHTML = '<p class="ha-section-label">Recommended for you</p>';
      const grid = document.createElement('div');
      grid.className = 'ha-products-grid';
      products.slice(0, Math.min(products.length, 3)).forEach((p) => {
        const item = document.createElement('div');
        item.className = 'ha-product-card ha-product-locked';
        item.innerHTML = `
          <div class="ha-product-top">
            <div class="ha-product-meta" style="filter:blur(5px);user-select:none;pointer-events:none;">
              <h4 class="ha-product-name">${this._esc(p.name || p.product_name || '')}</h4>
            </div>
            <div class="ha-lock-icon">🔒</div>
          </div>
        `;
        grid.appendChild(item);
      });
      sec.appendChild(grid);
      el.appendChild(sec);
    }

    // Unlock CTA
    const cta = document.createElement('div');
    cta.className = 'ha-unlock-wrap';
    cta.innerHTML = `
      <button class="ha-unlock-btn">&#x2726;&nbsp; Unlock Your Results</button>
      <p class="ha-unlock-sub">Get your complete hair analysis &amp; product picks via WhatsApp</p>
    `;
    cta.querySelector('.ha-unlock-btn').addEventListener('click', () => this._showOtpGate());
    el.appendChild(cta);
  }

  // ── Full results (post-OTP unlock) ────────────────────────────────────────────
  _renderFull(data, el) {
    const s = data.staging || {};
    const products = data.recommended_products || [];
    const confidence = Math.round((s.confidence || 0) * 100);

    const card = document.createElement('div');
    card.className = 'ha-assessment-card';
    card.innerHTML = `
      <p class="ha-section-label">Assessment Result</p>
      <div class="ha-stage-header">
        <div class="ha-stage-badge">
          <div class="ha-stage-badge-label">Stage</div>
          <div class="ha-stage-badge-value">${s.stage_numeric ?? '—'}</div>
          <div class="ha-stage-badge-scale">${this._fmt(s.scale)}</div>
        </div>
        <div class="ha-stage-info">
          <h3 class="ha-stage-name">${s.stage || ''}</h3>
          <p class="ha-confidence-row">Confidence: ${confidence}%</p>
          <div class="ha-confidence-bar">
            <div class="ha-confidence-fill" style="width:${confidence}%"></div>
          </div>
        </div>
      </div>
      ${s.regions?.length ? `<div class="ha-regions">${s.regions.map((r) => `<span class="ha-region-tag">${r.replace(/_/g, ' ')}</span>`).join('')}</div>` : ''}
      ${s.pattern_description ? `<p class="ha-description">${s.pattern_description}</p>` : ''}
      ${s.tags?.length ? `<div class="ha-tags">${s.tags.map((t) => `<span class="ha-tag">${t.replace(/_/g, ' ')}</span>`).join('')}</div>` : ''}
      ${s.disclaimer ? `<p class="ha-disclaimer">${s.disclaimer}</p>` : ''}
      ${s.caveats?.length ? `<details class="ha-caveats"><summary>Notes &amp; caveats (${s.caveats.length})</summary><ul>${s.caveats.map((c) => `<li>${c}</li>`).join('')}</ul></details>` : ''}
    `;
    el.appendChild(card);

    if (products.length) {
      const shopifyMap = this._buildShopifyMap();
      const sec = document.createElement('div');
      sec.innerHTML = '<p class="ha-section-label">Recommended for you</p>';
      const grid = document.createElement('div');
      grid.className = 'ha-products-grid';
      [...products]
        .sort((a, b) => (b.priority || 0) - (a.priority || 0))
        .forEach((p) => {
          const live = shopifyMap[p.id] || {};
          grid.appendChild(this._productCard({ ...p, ...live }));
        });
      sec.appendChild(grid);
      el.appendChild(sec);
    }
  }

  // ── OTP gate overlay ──────────────────────────────────────────────────────────
  _showOtpGate() {
    this.root.querySelector('.ha-otp-gate')?.remove();

    // Derive base URL: strip last path segment from apiEndpoint
    // e.g. https://hairtwin.beardsnshears.in/v1/hair-assessment → https://hairtwin.beardsnshears.in/v1
    const otpBase = this.apiEndpoint.replace(/\/[^/]+$/, '');

    const gate = document.createElement('div');
    gate.className = 'ha-otp-gate';
    gate.innerHTML = `
      <div class="ha-otp-inner">

        <!-- Phase 1: contact details -->
        <div class="ha-otp-phase" id="ha-otp-ph1">
          <div class="ha-otp-icon">
            <svg width="22" height="22" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M5.80023 10.2433C5.80023 9.691 6.24795 9.24329 6.80023 9.24329C7.35252 9.24329 7.80023 9.691 7.80023 10.2433C7.80023 10.7956 7.35252 11.2433 6.80023 11.2433C6.24795 11.2433 5.80023 10.7956 5.80023 10.2433Z" fill="currentColor"/>
              <path d="M9.22974 10.2433C9.22974 9.691 9.67745 9.24329 10.2297 9.24329C10.782 9.24329 11.2297 9.691 11.2297 10.2433C11.2297 10.7956 10.782 11.2433 10.2297 11.2433C9.67745 11.2433 9.22974 10.7956 9.22974 10.2433Z" fill="currentColor"/>
              <path d="M12.6592 10.2433C12.6592 9.691 13.1069 9.24329 13.6592 9.24329C14.2115 9.24329 14.6592 9.691 14.6592 10.2433C14.6592 10.7956 14.2115 11.2433 13.6592 11.2433C13.1069 11.2433 12.6592 10.7956 12.6592 10.2433Z" fill="currentColor"/>
              <path fill-rule="evenodd" clip-rule="evenodd" d="M16.6445 17.2036C16.6418 17.2023 16.6392 17.2009 16.6365 17.1996C16.3947 17.0764 16.1302 16.9257 15.8701 16.7774C15.2251 16.4099 14.607 16.0576 14.4279 16.1795C13.2314 16.9937 11.7861 17.4696 10.2297 17.4696C6.10323 17.4696 2.75806 14.1244 2.75806 9.99791C2.75806 5.87142 6.10323 2.52625 10.2297 2.52625C14.3562 2.52625 17.7014 5.87142 17.7014 9.99791C17.7014 11.512 17.251 12.9209 16.477 14.0979C16.3445 14.2994 16.6705 14.9403 17.0113 15.6104C17.1583 15.8994 17.308 16.1938 17.4249 16.4607C17.4269 16.4653 17.4289 16.4697 17.4308 16.4742C17.6237 16.9172 17.7239 17.2824 17.5671 17.418C17.4394 17.5544 17.0791 17.4243 16.6445 17.2036ZM18.2593 18.14C17.8792 18.5115 17.4018 18.4853 17.2128 18.461C16.9786 18.4309 16.7562 18.3504 16.587 18.2799C16.2391 18.1348 15.8412 17.9114 15.506 17.7209C15.4611 17.6954 15.4172 17.6704 15.3743 17.6459C15.0837 17.4804 14.8356 17.3391 14.6299 17.2385C13.347 18.0196 11.8396 18.4696 10.2297 18.4696C5.55095 18.4696 1.75806 14.6767 1.75806 9.99791C1.75806 5.31914 5.55095 1.52625 10.2297 1.52625C14.9085 1.52625 18.7014 5.31914 18.7014 9.99791C18.7014 11.5821 18.2656 13.0672 17.5072 14.3369C17.5098 14.3432 17.5125 14.3497 17.5153 14.3564C17.6071 14.5755 17.7411 14.8392 17.8997 15.1513C17.927 15.2049 17.9549 15.2599 17.9835 15.3164C18.1621 15.6689 18.3719 16.0905 18.5003 16.4618C18.5631 16.6435 18.6314 16.8815 18.6427 17.1309C18.6529 17.3561 18.6228 17.7987 18.2593 18.14Z" fill="currentColor"/>
            </svg>
          </div>
          <h3 class="ha-otp-title">Unlock your results</h3>
          <p class="ha-otp-desc">Enter your details to receive your complete hair analysis on WhatsApp</p>
          <div class="ha-field" style="margin-bottom:14px;">
            <label for="ha-otp-name-inp">First name</label>
            <input type="text" id="ha-otp-name-inp" placeholder="Your first name" autocomplete="given-name">
          </div>
          <div class="ha-field" style="margin-bottom:14px;">
            <label for="ha-otp-phone-inp">WhatsApp number</label>
            <div class="ha-phone-wrap">
              <span class="ha-phone-prefix">+91</span>
              <input type="tel" id="ha-otp-phone-inp" placeholder="10-digit number" maxlength="10" inputmode="numeric">
            </div>
          </div>
          <label class="ha-consent-wrap">
            <input type="checkbox" id="ha-otp-consent">
            <span class="ha-consent-text">
              I agree to receive my OTP, hair analysis results, and periodic progress check-ins on WhatsApp from <strong>Beards &amp; Shears</strong>. Your number is used only for this purpose. View our <a href="/policies/privacy-policy" target="_blank" rel="noopener" class="ha-consent-link">Privacy Policy</a>.
            </span>
          </label>
          <div id="ha-otp-send-err" class="ha-otp-error ha-hidden"></div>
          <button class="ha-btn ha-btn-primary" id="ha-otp-send-btn" disabled>Send OTP via WhatsApp &nbsp;›</button>
          <p class="ha-otp-stop-note">Reply STOP on WhatsApp anytime to opt out.</p>
          <button class="ha-otp-cancel-link" id="ha-otp-cancel-btn">Cancel</button>
        </div>

        <!-- Phase 2: OTP input -->
        <div class="ha-otp-phase ha-hidden" id="ha-otp-ph2">
          <div class="ha-otp-icon">🔑</div>
          <h3 class="ha-otp-title">Enter OTP</h3>
          <p class="ha-otp-desc" id="ha-otp-ph2-desc">We sent a 6-digit code to your WhatsApp</p>
          <div class="ha-field" style="margin-bottom:20px;">
            <label for="ha-otp-code-inp">OTP Code</label>
            <input type="text" id="ha-otp-code-inp" placeholder="· · · · · ·" maxlength="6" inputmode="numeric" class="ha-otp-code-input">
          </div>
          <div id="ha-otp-verify-err" class="ha-otp-error ha-hidden"></div>
          <button class="ha-btn ha-btn-primary" id="ha-otp-verify-btn">Verify &amp; Unlock &nbsp;&#x2726;</button>
          <button class="ha-otp-cancel-link" id="ha-otp-resend-btn">&#8592; Change number / Resend</button>
        </div>

      </div>
    `;

    const ph1 = gate.querySelector('#ha-otp-ph1');
    const ph2 = gate.querySelector('#ha-otp-ph2');
    const sendErr   = gate.querySelector('#ha-otp-send-err');
    const verifyErr = gate.querySelector('#ha-otp-verify-err');

    // Checkbox toggles send button enabled state
    gate.querySelector('#ha-otp-consent').addEventListener('change', (e) => {
      const btn = gate.querySelector('#ha-otp-send-btn');
      btn.disabled = !e.target.checked;
    });

    // Cancel → remove gate
    gate.querySelector('#ha-otp-cancel-btn').addEventListener('click', () => gate.remove());

    // Back / resend → return to phase 1
    gate.querySelector('#ha-otp-resend-btn').addEventListener('click', () => {
      ph2.classList.add('ha-hidden');
      ph1.classList.remove('ha-hidden');
      sendErr.classList.add('ha-hidden');
      verifyErr.classList.add('ha-hidden');
    });

    // Send OTP
    gate.querySelector('#ha-otp-send-btn').addEventListener('click', async () => {
      const name  = gate.querySelector('#ha-otp-name-inp').value.trim();
      const phone = gate.querySelector('#ha-otp-phone-inp').value.trim();

      sendErr.classList.add('ha-hidden');
      if (!name) { sendErr.textContent = 'Please enter your first name.'; sendErr.classList.remove('ha-hidden'); return; }
      if (!/^\d{10}$/.test(phone)) { sendErr.textContent = 'Enter a valid 10-digit WhatsApp number.'; sendErr.classList.remove('ha-hidden'); return; }

      const btn = gate.querySelector('#ha-otp-send-btn');
      btn.disabled = true;
      btn.textContent = 'Sending…';

      try {
        const res = await fetch(`${otpBase}/otp/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone, first_name: name }),
        });
        if (!res.ok) throw new Error('Failed to send OTP');
        this._otpPhone = phone;
        gate.querySelector('#ha-otp-ph2-desc').textContent = `We sent a 6-digit code to +91 ${phone}`;
        ph1.classList.add('ha-hidden');
        ph2.classList.remove('ha-hidden');
        gate.querySelector('#ha-otp-code-inp').focus();
      } catch {
        sendErr.textContent = 'Could not send OTP. Please try again.';
        sendErr.classList.remove('ha-hidden');
      } finally {
        btn.disabled = false;
        btn.textContent = 'Send OTP via WhatsApp ›';
      }
    });

    // Verify OTP
    gate.querySelector('#ha-otp-verify-btn').addEventListener('click', async () => {
      const code = gate.querySelector('#ha-otp-code-inp').value.trim();
      verifyErr.classList.add('ha-hidden');
      if (!/^\d{6}$/.test(code)) { verifyErr.textContent = 'Enter the 6-digit OTP.'; verifyErr.classList.remove('ha-hidden'); return; }

      const btn = gate.querySelector('#ha-otp-verify-btn');
      btn.disabled = true;
      btn.textContent = 'Verifying…';

      try {
        const res = await fetch(`${otpBase}/otp/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ phone: this._otpPhone, code }),
        });
        if (!res.ok) throw new Error('Invalid OTP');

        localStorage.setItem('ha_verified', '1');
        gate.remove();

        // Swap preview for full results
        const el = this.els.resultsEl;
        // Remove everything after the returning banner (if any)
        const banner = el.querySelector('.ha-returning-banner');
        el.innerHTML = '';
        if (banner) el.appendChild(banner);
        if (this._pendingData) this._renderFull(this._pendingData, el);
      } catch {
        verifyErr.textContent = 'Incorrect OTP. Please try again.';
        verifyErr.classList.remove('ha-hidden');
        btn.disabled = false;
        btn.textContent = 'Verify & Unlock ✦';
      }
    });

    // Append gate to dialog-inner so it overlays the content
    const inner = this.root.querySelector('.ha-dialog-inner');
    inner.appendChild(gate);
    gate.querySelector('#ha-otp-name-inp').focus();
  }

  _productCard(product) {
    const name = product.name || product.product_name || '';
    const category = (product.category || '').replace(/_/g, ' ');
    const desc = product.description || product.product_description || '';
    const rationale = product.rationale || '';
    const originalPrice = product.original_price ? parseFloat(product.original_price) : null;
    const discountedPrice = product.discounted_price ? parseFloat(product.discounted_price) : null;
    const currency = product.currency || '';
    const benefits = Array.isArray(product.key_benefits) ? product.key_benefits.filter(Boolean) : [];
    const howToUse = product.how_to_use || '';
    const ingredients = product.ingredients || '';
    const warnings = product.warnings || '';
    const url = product.url || '';

    // Price section — only if we have a price
    let priceHtml = '';
    if (discountedPrice) {
      const hasDiscount = originalPrice && originalPrice > discountedPrice;
      const pctOff = hasDiscount ? Math.round((1 - discountedPrice / originalPrice) * 100) : 0;
      priceHtml = `
        <div class="ha-product-price">
          <span class="ha-price-current">${currency} ${discountedPrice.toFixed(2)}</span>
          ${hasDiscount ? `<span class="ha-price-original">${currency} ${originalPrice.toFixed(2)}</span>` : ''}
          ${hasDiscount ? `<span class="ha-price-badge">-${pctOff}%</span>` : ''}
        </div>`;
    }

    // Body sections — only rendered when non-empty
    const bodyParts = [];

    if (desc.trim()) {
      bodyParts.push(`<p class="ha-product-desc">${this._esc(desc)}</p>`);
    }

    if (rationale.trim()) {
      bodyParts.push(`<p class="ha-product-rationale">${this._esc(rationale)}</p>`);
    }

    if (benefits.length > 0) {
      bodyParts.push(`
        <div>
          <p class="ha-product-sub-label">Key Benefits</p>
          <ul class="ha-product-benefits">
            ${benefits.map((b) => `<li>${this._esc(b)}</li>`).join('')}
          </ul>
        </div>`);
    }

    if (howToUse.trim()) {
      bodyParts.push(`
        <div>
          <p class="ha-product-sub-label">How to Use</p>
          <div class="ha-product-howtouse">${this._richText(howToUse)}</div>
        </div>`);
    }

    if (ingredients.trim()) {
      bodyParts.push(`<p class="ha-product-ingredients">Active ingredients: ${this._esc(ingredients)}</p>`);
    }

    if (warnings.trim()) {
      bodyParts.push(`<p class="ha-product-warning">⚠ ${this._esc(warnings)}</p>`);
    }

    const cartSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"/></svg>`;

    const card = document.createElement('div');
    card.className = 'ha-product-card';
    card.innerHTML = `
      <div class="ha-product-top">
        <div class="ha-product-meta">
          ${category ? `<span class="ha-product-category">${category}</span>` : ''}
          <h4 class="ha-product-name">${this._esc(name)}</h4>
          ${priceHtml}
        </div>
        <div class="ha-product-actions">
          <button class="ha-add-to-cart">${cartSvg} Add to Cart</button>
          ${url ? `<a class="ha-view-product" href="${url}" target="_blank" rel="noopener">View</a>` : ''}
        </div>
      </div>
      ${bodyParts.length > 0 ? `<div class="ha-product-body">${bodyParts.join('')}</div>` : ''}
    `;

    card.querySelector('.ha-add-to-cart').addEventListener('click', (e) => this._addToCart(e.currentTarget, product));
    return card;
  }

  // Render a value that may be plain text or Shopify rich text JSON
  _richText(value) {
    const str = String(value || '').trim();
    if (!str.startsWith('{')) return `<p>${this._esc(str)}</p>`;
    try {
      return this._rtNode(JSON.parse(str));
    } catch {
      return `<p>${this._esc(str)}</p>`;
    }
  }

  // Recursively render a Shopify rich text node to HTML
  _rtNode(node) {
    if (!node) return '';
    const kids = () => (node.children || []).map((c) => this._rtNode(c)).join('');
    switch (node.type) {
      case 'root':      return kids();
      case 'paragraph': return `<p>${kids()}</p>`;
      case 'list':      return node.listType === 'ordered'
                          ? `<ol>${kids()}</ol>`
                          : `<ul>${kids()}</ul>`;
      case 'list-item': return `<li>${kids()}</li>`;
      case 'heading':   { const l = Math.min(6, Math.max(1, node.level || 2)); return `<h${l}>${kids()}</h${l}>`; }
      case 'link':      return `<a href="${this._esc(node.url || '')}" target="_blank" rel="noopener">${kids()}</a>`;
      case 'text': {
        let t = this._esc(node.value || '');
        if (node.bold)   t = `<strong>${t}</strong>`;
        if (node.italic) t = `<em>${t}</em>`;
        if (node.underline) t = `<u>${t}</u>`;
        return t;
      }
      default: return kids();
    }
  }

  // Safe HTML escape for user/API content
  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // Build map of GID → live Shopify data from window.__haProducts
  _buildShopifyMap() {
    const map = {};
    (window.__haProducts || []).forEach((p) => { if (p.id) map[p.id] = p; });
    return map;
  }

  async _addToCart(btn, product) {
    const originalHtml = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = `<span class="ha-spinner" style="width:14px;height:14px;border-width:2px"></span>`;

    try {
      // Fast path: variant_id already embedded via Liquid-rendered product data
      let variantId = product.variant_id;

      // Slow path: product came from API only, resolve variant by store search
      if (!variantId) {
        const q = encodeURIComponent(product.name || product.product_name || '');
        const res = await fetch(`/search/suggest.json?q=${q}&resources[type]=product&resources[limit]=3`);
        const data = await res.json();
        const found = data?.resources?.results?.products?.[0];
        if (found) variantId = await this._firstVariantId(found.handle);
      }

      if (!variantId) throw new Error('variant-not-found');

      await this._cartAdd(variantId, btn);
    } catch {
      // Never navigate away to the product page — keep the user inside the
      // widget and let them retry the add in place.
      this._cartAddFailed(btn, originalHtml);
    }
  }

  // Restore the button to a retryable state after a failed add.
  _cartAddFailed(btn, originalHtml) {
    btn.disabled = false;
    btn.classList.remove('is-added');
    btn.textContent = "Couldn't add — try again";
    setTimeout(() => {
      btn.innerHTML = originalHtml;
    }, 2400);
  }

  async _firstVariantId(handle) {
    try {
      const r = await fetch(`/products/${handle}.js`);
      return (await r.json())?.variants?.[0]?.id || null;
    } catch { return null; }
  }

  async _cartAdd(variantId, btn) {
    // Ask Shopify to re-render the cart-item sections in the same round trip so
    // the drawer can morph in place — same contract the theme's product form uses.
    const sectionIds = this._cartSectionIds();

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        ...(sectionIds.length ? { sections: sectionIds, sections_url: window.location.pathname } : {}),
      }),
    });
    if (!res.ok) throw new Error();

    const addResponse = await res.json().catch(() => ({}));

    btn.disabled = false;
    btn.className = 'ha-add-to-cart is-added';
    btn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/></svg>
      Added!
    `;

    // Fetch the authoritative cart so the bubble/drawer reflect the true total,
    // then fire the theme's own cart:update event (see assets/events.js). The
    // cart icon, cart drawer (auto-open) and cart-items components all listen
    // for this — the legacy cart:refresh/cart:updated events had no listeners.
    let cart = null;
    try {
      cart = await (await fetch('/cart.js')).json();
    } catch { /* keep cart null; event still updates the drawer via sections */ }

    this._dispatchCartUpdate(cart, variantId, addResponse.sections);

    // Slide in the theme's own cart drawer, exactly like a normal add-to-cart.
    // It's a top-layer modal, so it stacks above the assessment dialog and the
    // user can keep adding more recommendations after closing it.
    this._openCartDrawer();
  }

  // Open the theme's cart drawer regardless of the merchant's auto-open setting.
  // showDialog() is idempotent (no-ops if already open), so this is safe even
  // when auto-open already handled the cart:update event above.
  _openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.showDialog === 'function') drawer.showDialog();
  }

  // Section IDs of every cart-items-component on the page (drawer + cart page),
  // used to request pre-rendered section HTML from /cart/add.js.
  _cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      const id = el.getAttribute('data-section-id');
      if (id && !ids.includes(id)) ids.push(id);
    });
    return ids;
  }

  // Dispatch the theme's cart:update event with the shape assets/events.js expects:
  // { resource: <cart>, sourceId, data: { source, itemCount, variantId, sections } }
  _dispatchCartUpdate(cart, variantId, sections) {
    const detail = {
      resource: cart || undefined,
      sourceId: 'hair-assessment-widget',
      data: {
        // NOT 'product-form-component' — the cart icon *adds* itemCount for that
        // source; any other source sets the count to the cart's true total.
        source: 'hair-assessment-widget',
        itemCount: cart ? cart.item_count : 1,
        variantId: String(variantId),
        sections: sections || undefined,
      },
    };

    // Mirror the theme's own events (assets/events.js): a plain Event with a
    // `detail` own-property. CustomEvent.detail is read-only, so we can't use it.
    const evt = new Event('cart:update', { bubbles: true });
    evt.detail = detail;
    document.dispatchEvent(evt);
  }

  _fmt(str) {
    return (str || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  _reset() {
    this.photos = [];
    this._stopCamera();
    this.els.resultsEl && (this.els.resultsEl.innerHTML = '');
    this.els.errorEl?.classList.add('ha-hidden');
    // Clear saved session and verification so next open starts fresh
    localStorage.removeItem('ha_session_id');
    localStorage.removeItem('ha_anon_id');
    localStorage.removeItem('ha_verified');
    this._goToStep(IS_MOBILE ? 2 : 1);
    if (IS_MOBILE) setTimeout(() => this._startCamera(), 80);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-ha-widget]').forEach((el) => new HairAssessmentWidget(el));
});
